import { ApplicationNavigationService } from 'app/services/shell/application-navigation/application-navigation.service';
import { Injectable, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpHeaders, HttpParams, HttpContext } from '@angular/common/http';
import { EupRoutesService, RoutingData } from '@core/eupRoutes.service';
import { Observable, BehaviorSubject, throwError, of, EMPTY, from, withLatestFrom } from 'rxjs';
import { catchError, map, switchMap, tap, take, finalize, defaultIfEmpty } from 'rxjs/operators';
import { GlobalSettings, GlobalSettingsService } from '@core/globalSettings.service';
import { EupHttpHandler } from '@core/eupHttpHandler.service';
import { Company, ContactWithBusinessPartners } from '@shared/generalInterfaces';
import { Params } from '@angular/router';
import { AuthenticationStatus } from './models/authentication-status';
import { IOSimSimulationInfoStore } from '../iosim-simulation.store/iosim-simulation-status-progress.store';
import { Consts } from '@shared/consts';
import { DownloadNotificationService } from '@shared/downloadNotification/downloadNotification.service';
import { LogoutParameters } from './models/logout-parameters';
import { FeatureToggle, SoftwareOptionsForCompany, RoleTypeEnum } from '@shared/enums';
import { SpinnerService } from '@core/spinner/spinner.service';
import { ShellContextService } from '../shell-context/shell-context.service';
import { CommunicationService } from '../client-communication/communication.service';
import { AppUpdater } from '@shared/appUpdater.service';
import { StickyHeaderService } from '../stickyHeaderService/stickyHeader.service';
import { PendoService } from '@shared/pendo/pendo.service';
import { FeatureToggleSettings } from 'app/featuresToggleSettings/featuresToggleSettings.model';
import { FeaturesToggleSettingsService } from 'app/featuresToggleSettings/service/featuresToggleSettings.service';
import { TimberService } from '@logging/timber.service';
import { SoftwareOptionsService } from 'app/core/companySoftwareOptions/service/softwareOptions.service';
import { LabService } from 'app/labs/lab.service';
import { AuthProvider, CompanyType, CookieName, AUTH_ANGULAR__FORCE_REQUEST_WITH_EXPIRED_TOKEN, AuthAngularService } from '@nodeModules/@itero/auth-angular';
import { ClientDiscoveryService } from 'app/platform/services/client-discovery/client-discovery.service';
import { KnownComponentsNames } from '@core/components-names';
import { CookieService } from '@core/cookie.service';
import { IKeyValuePair } from '@interfaces/IKeyValuePair';
import { ShowAllService } from '@shared/show-all-toggle/show-all.service';
import { AuthCookiesService } from '@shared/authCookies.service';
import { Utils } from '@shared/utils.service';
import { AccessibilityService } from '@shared/accessibility.service';
import { PlatformLoginService } from '../platform-login/platform-login.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService {

	constructor(
		private http: EupHttpHandler,
		private eupRoutesService: EupRoutesService,
		private globalSettingsService: GlobalSettingsService,
		private platformLoginService: PlatformLoginService,
		private simulationStatusProgressStore: IOSimSimulationInfoStore,
		private downloadNotificationService: DownloadNotificationService,
		private spinnerService: SpinnerService,
		private shellContextService: ShellContextService,
		private communicationService: CommunicationService,
		private appUpdater: AppUpdater,
		private applicationNavigationService: ApplicationNavigationService,
		private labService: LabService,
		private stickyHeaderService: StickyHeaderService,
		private pendoService: PendoService,
		private featuresToggleSettingsService: FeaturesToggleSettingsService,
		private softwareOptionsService: SoftwareOptionsService,
		private timberService: TimberService,
		private cookieAuthService: AuthCookiesService,
		private cookieService: CookieService,
		private authAngularService: AuthAngularService,
		private readonly showAllService: ShowAllService,
		private readonly utils: Utils,
		private readonly accessibilityService: AccessibilityService,
	) {
		this.authStatus$ = new BehaviorSubject<AuthenticationStatus>({ isAuthenticated: this.isAuthenticated() });

		// Keep app-level session storage in sync with @itero/auth-angular refreshes.
		// The library updates its internal auth state, but many parts of this app still read from localStorage / shell context.
		this.authAngularService.refreshSucceed$?.pipe(
			withLatestFrom(this.featuresToggleSettingsService.isVisibleFeature$(FeatureToggle.UseIteroPlatformLogin)),
			takeUntilDestroyed(inject(DestroyRef))
		).subscribe(([authInfo, useIteroPlatformLogin]) => {
			try {
				const raw = localStorage.getItem(Consts.Storage.SessionInfo);
				if (raw) {
					const session = JSON.parse(raw) as SessionInfo;
					session.accessToken = authInfo.accessToken;
					session.sessionId = authInfo.sessionId;
					if (authInfo.idToken) {
						session.idToken = authInfo.idToken;
					}
					localStorage.setItem(Consts.Storage.SessionInfo, JSON.stringify(session));

					if (useIteroPlatformLogin) {
						const username = this.platformLoginService.getEmailFromAccessToken(authInfo.accessToken);
						if (username) {
							this.platformLoginService.updateToken(username, authInfo.sessionId, authInfo.accessToken);
						}
					}
				}
			} catch (error) {
				// Best-effort sync only. Never break the refresh flow, but record diagnostics.
				this.timberService.warn('Failed to sync refreshed auth info into localStorage SessionInfo', {
					module: 'AuthService',
					error,
				});
			}

			this.shellContextService.updateContext((context) => {
				context.session.sessionId = authInfo.sessionId;
				context.security.accessToken = authInfo.accessToken;
			});
		});
	}
	private appId = 'midc';

	authStatus$: BehaviorSubject<AuthenticationStatus>;
	private invalidateSessionUrl = (sessionId: string) =>
		`${this.eupRoutesService.iTeroWebAuthApiUrl}session/invalidate?sessionId=${sessionId}&appId=${this.appId}`

	startSession(credentials: UserCredentials): Observable<any> {
		const startSessionUrl = `${this.eupRoutesService.iTeroWebAuthApiUrl}session/start?appId=${this.appId}`;
		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});

		const startSession$ = this.http.post(
			startSessionUrl,
			{ username: credentials.username, password: credentials.password },
			{ headers, observe: 'response' }
		);
		const auth = () =>
			startSession$.pipe(
				map((res) => res.body),
				catchError((err) => this.throwError(err))
			);

		return this.eupRoutesService.doAfterInit(auth);
	}

	proceedLoginFlow(sessionInfo: SessionInfo, credentials: UserCredentials, authProvider: AuthProvider): Observable<GlobalSettings | any> {
		let headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});
		
		
		localStorage.setItem(
			Consts.Storage.SessionInfo,
			JSON.stringify({
				accessToken: sessionInfo.accessToken,
				sessionId: sessionInfo.sessionId,
				sessionType: Consts.SessionType,
				idToken: sessionInfo.idToken,
			} as SessionInfo)
		);
		this.initializeAuth(sessionInfo);

		headers = headers.set(Consts.HeaderKeys.Authorization, `Bearer ${sessionInfo.accessToken}`);
		const login$ = this.prepareLoginObservable(credentials, sessionInfo, headers);
		const getUserData$ = this.prepareGetUserDataObservable(credentials, headers);

		return login$.pipe(
			switchMap(() => this.handleUserLogin(getUserData$, headers, sessionInfo, authProvider)),
			take(1),
			catchError((error) => {
				this.timberService.error('proceedLoginFlow: Error in login flow', { 
					module: 'AuthService',
					error: error,
					extendedParameters: { 
						sessionId: sessionInfo?.sessionId || 'none',
						authProvider: authProvider || 'none',
						errorStatus: error?.status?.toString() || 'unknown',
						errorMessage: error?.message || 'unknown'
					}
				});
				if (sessionInfo.sessionId.trim() !== '') {
					return this.invalidateOrLogoutSession(sessionInfo.sessionId).pipe(switchMap(() => this.throwError(error)));
				}
				return this.throwError(error);
			})
		);
	}

	proceedLoginFlowV2(sessionInfo: SessionInfo, credentials: UserCredentials, region: string): Observable<GlobalSettings | any> {
		let headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});
		localStorage.setItem(
			Consts.Storage.SessionInfo,
			JSON.stringify({
				accessToken: sessionInfo.accessToken,
				sessionId: sessionInfo.sessionId,
				sessionType: Consts.SessionType,
				idToken: sessionInfo.idToken,
			} as SessionInfo)
		);
		this.initializeAuth(sessionInfo);

		headers = headers.set(Consts.HeaderKeys.Authorization, `Bearer ${sessionInfo.accessToken}`);
		const login$ = this.prepareLoginObservable(credentials, sessionInfo, headers);

		return login$.pipe(
			switchMap(() => this.prepareGetUserDataObservable(credentials, headers)),
			map((data) => data.body),
			tap((data) => this.setUserLogin(data, sessionInfo, region)),
			take(1),
			catchError((error) => {
				if (sessionInfo.sessionId.trim() !== '') {
					return this.invalidateOrLogoutSession(sessionInfo.sessionId).pipe(switchMap(() => this.throwError(error)));
				}
				return this.throwError(error);
			})
		);
	}



		/**
	 * Restore auth state from localStorage.
	 * Called by APP_INITIALIZER before app bootstrap and as safety check in app.component
	 */
	public restoreAuthFromStorage(): void {
		try {
			const sessionInfo = localStorage.getItem(Consts.Storage.SessionInfo);
			if (sessionInfo) {
				const session = JSON.parse(sessionInfo) as SessionInfo;
				this.initializeAuth(session);
			}
		} catch (error) {
			console.error('Failed to restore auth from localStorage:', error);
		}
	}

	private initializeAuth(sessionInfo: SessionInfo): void {
		// Initialize auth info if we have session data
		if (sessionInfo) {
			try {
				if (sessionInfo.accessToken) {
					console.log('AUTH: Initializing auth library with session data');

					// Get the auth API URL from routes
					const routes = JSON.parse(localStorage.getItem(Consts.Storage.Routes) || '{}');
					const authUrl = routes?.iTeroWebAuthApiUrl;

					this.authAngularService.updateAuthInfo({
						sessionId: sessionInfo.sessionId || 'current',
						sessionType: sessionInfo.sessionType || 'standard',
						accessToken: sessionInfo.accessToken,
						authUrl: authUrl
					});
				}
			} catch (error) {
				console.error('AUTH: Failed to parse session info:', error);
			}
		}
	}

	/**
	 * Safety check: if the auth-angular interceptor has no token in its internal state
	 * but localStorage has a valid session, re-initialize the library.
	 * This covers edge cases where internal state was lost (e.g., after a failed refresh cycle).
	 */
	private ensureAuthInitialized(): void {
		if (!this.authAngularService.authInfo?.accessToken) {
			this.restoreAuthFromStorage();
		}
	}

	private prepareLoginObservable(credentials: UserCredentials, sessionInfo: SessionInfo, headers: HttpHeaders): Observable<any> {
		const loginUrl = this.eupRoutesService.login.loginToMidc;

		return this.http.post(
			loginUrl,
			{ username: credentials.username, password: credentials.password, sessionId: sessionInfo.sessionId },
			{ headers, observe: 'response' }
		).pipe(
			tap(() => {
				this.timberService.info('prepareLoginObservable: loginToMidc successful', { module: 'AuthService' });
			}),
			catchError(error => {
				this.timberService.error('prepareLoginObservable: Error calling loginToMidc', { 
					module: 'AuthService',
					error,
					extendedParameters: { 
						loginUrl: loginUrl || 'unknown',
						errorStatus: error?.status?.toString() || 'unknown',
						errorMessage: error?.message || 'unknown'
					}
				});
				return throwError(() => error);
			})
		);
	}

	private prepareGetUserDataObservable(credentials: UserCredentials, headers: HttpHeaders): Observable<any> {
		const getUserDataUrl = this.eupRoutesService.login.getUserData(credentials?.username || '');
		
		this.timberService.info('prepareGetUserDataObservable: requesting getUserData', { 
			module: 'AuthService',
			extendedParameters: { 
				UserDataUrl: getUserDataUrl
			}
		});
		
		return this.http.get<{ body: GlobalSettings }>(getUserDataUrl, {
			headers,
			observe: 'response',
		}).pipe(
			tap((response) => {
				this.timberService.info('prepareGetUserDataObservable: getUserData raw response', { 
					module: 'AuthService',
					extendedParameters: { 
						rawResponse: JSON.stringify(response?.body)
					}
				});
				this.timberService.info('prepareGetUserDataObservable: getUserData response received', { 
					module: 'AuthService',
					extendedParameters: { 
						username: credentials?.username ?? 'unknown',
						hasBody: (!!response?.body).toString(),
						hasCompanies: (!!response?.body?.companies).toString(),
						companiesCount: response?.body?.companies?.length?.toString() || '0'
					}
				});
			}),
			catchError(error => {
				this.timberService.error('prepareGetUserDataObservable: Error calling getUserData', { 
					module: 'AuthService',
					error,
					extendedParameters: { 
						getUserDataUrl: getUserDataUrl || 'unknown',
						errorStatus: error?.status?.toString() || 'unknown'
					}
				});
				return throwError(() => error);
			})
		);
	}

	prepareGetUserCompanyDataObservable(headers: HttpHeaders, companyId: number): Observable<any> {
		return this.http.get<{ body: IKeyValuePair }>(this.eupRoutesService.login.getUserCompanyData(companyId), {
			headers,
			observe: 'response',
		});
	}

	private handlePlatformSelectorEnabled(
		companyId: number,
		headers: HttpHeaders,
		role: RoleTypeEnum,
		sessionInfo: SessionInfo,
		authProvider: AuthProvider,
		userData: GlobalSettings,
	): Observable<any> {
		return this.prepareGetUserCompanyDataObservable(headers, companyId).pipe(
			map((data) => data.body),
			switchMap((cswo: IKeyValuePair) => {
				switch (role) {
					case RoleTypeEnum.Lab:
						if (Object.values(cswo).includes(SoftwareOptionsForCompany.cswo_UseMiflPlatform)) {
							return this.redirectToPlatform(sessionInfo, CompanyType.Lab, KnownComponentsNames.MyiteroLab, authProvider, companyId);
						}
						this.setUserLogin(userData, sessionInfo);
						return of(userData);
					case RoleTypeEnum.ManagementCompany:
					case RoleTypeEnum.Partner:
						const isDsoEntSwo = Object.values(cswo).includes(SoftwareOptionsForCompany.cswo_DSO_Enterprise);
						const UseEntPlatformSwo = Object.values(cswo).includes(SoftwareOptionsForCompany.cswo_UseEntPlatform);

							if (isDsoEntSwo || UseEntPlatformSwo) {
								const companyType = role == RoleTypeEnum.Partner ? CompanyType.Partner : CompanyType.Dso;
								return this.redirectToPlatform(sessionInfo, companyType, KnownComponentsNames.MyiteroEnt, authProvider, companyId);
							}
							this.setUserLogin(userData, sessionInfo);
							return of(userData);
					default:
						this.setUserLogin(userData, sessionInfo);
						return of(userData)
				}
			}),
		);
	}
			
	private async redirectToPlatform(
		sessionInfo: SessionInfo,
		companyType: CompanyType,
		component: KnownComponentsNames, 
		authProvider?: AuthProvider,
		companyId?: number,
		): Promise<Observable<never>> {
		this.setCookies(sessionInfo, companyType, authProvider, companyId);
		const app = await ClientDiscoveryService.getClientApplication(component as string);
		const redirectBaseUrl = app?.url ?? null;
		const returnUrl = this.cookieService.get(Consts.CookiesNames.RETURNURL) ?? '';
		window.location.href = `${redirectBaseUrl}${returnUrl}`;
		return EMPTY;
	}

	private handleUserLogin(
		getUserData$: Observable<{ body: GlobalSettings }>,
		headers: HttpHeaders,
		sessionInfo: SessionInfo,
		authProvider?: AuthProvider,
	): Observable<any> {		
		return getUserData$.pipe(
			map((data) => data?.body),
			tap((data) => {
				this.timberService.info('handleUserLogin: User data mapped', { 
					module: 'AuthService',
					extendedParameters: { 
						hasData: (!!data).toString(),
						hasCompanies: (!!data?.companies).toString(),
						companiesIsArray: Array.isArray(data?.companies).toString(),
						companiesCount: data?.companies?.length?.toString() || '0',
						roleType: data?.roleType?.toString() ?? 'unknown',
						contactId: data?.contactId?.toString() ?? 'unknown'
					}
				});
			}),
			switchMap((data) => {
				if (!data?.companies) {
					this.timberService.error('handleUserLogin: companies is undefined in getUserData response', { 
						module: 'AuthService', 
						extendedParameters: { contactId: data?.contactId?.toString() || 'unknown' }
					});
				} else if (data.companies.length === 0) {
					this.timberService.error('handleUserLogin: companies array is empty in getUserData response', { 
						module: 'AuthService', 
						extendedParameters: { contactId: data?.contactId?.toString() || 'unknown' }
					});
				}
				
				const company = this.getDefaultCompany(data?.companies);
				if (!this.isSameRegion(company)) {
					const redirectObservable = this.redirect(company);
					if (redirectObservable) {
						return redirectObservable.pipe(switchMap(() => EMPTY));
					}
				}				
				
				const tokenRole = this.utils.getTokenRole(sessionInfo.accessToken);
				const role = tokenRole === data.roleType ? tokenRole : data.roleType;
				switch (role) {
					case RoleTypeEnum.Lab:
					case RoleTypeEnum.ManagementCompany:
					case RoleTypeEnum.Partner:
						return this.handleNonDoc(role, data, headers, sessionInfo, authProvider);
					default:
						this.setUserLogin(data, sessionInfo);
						return of(data);
				}
			}),
		);
	}

	handleNonDoc(
		role: RoleTypeEnum, 
		userData: GlobalSettings, 
		headers: HttpHeaders,
		sessionInfo: SessionInfo,
		authProvider: AuthProvider,
	): Observable<any> {
		return this.featuresToggleSettingsService.getFeaturesToggleSettings().pipe(
			take(1),
			map(() => this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.EnablePlatformSelector, false)),
			switchMap((isFeatureEnabled) => {
				if (isFeatureEnabled) {
					return this.handlePlatformSelectorEnabled(userData.companies[0].id, headers, role, sessionInfo, authProvider, userData);
				} else {
					this.setUserLogin(userData, sessionInfo);
					return of(userData);
				}
			}),
			catchError((error) => {
				// Fallback to setUserLogin if there's an error
                this.timberService.error(`Error handleNonDoc`, { module: 'AuthService', error: error });
				this.setUserLogin(userData, sessionInfo);
				return of(userData);
        	})
		)
	}

	setUserLogin(data, sessionInfo: SessionInfo, region: string = undefined) {
		this.communicationService.setSessionInfo(sessionInfo.sessionId);
		if (!data || !data?.companies) {
			this.timberService.error('setUserLogin: companies is undefined', { 
				module: 'AuthService', 
				extendedParameters: { contactId: data.contactId?.toString() }
			});
		} else if (region) {
			this.communicationService.setRegion(region);
		} else {
			const defaultCompany = this.getDefaultCompany(data.companies);
			this.communicationService.setRegion(defaultCompany.region);
		}

		this.shellContextService.updateContext((ctx) => {
			ctx.session.sessionId = sessionInfo.sessionId;
			ctx.security.accessToken = sessionInfo.accessToken;
			ctx.user.loggedInUserId = data.contactId;
			ctx.user.roleType = data.roleType;
			ctx.session.sessionType = Consts.SessionType;

			if (data.dateFormat || data.selectedLanguage?.code) {
				ctx.UI.dateFormat = data.dateFormat;
				ctx.UI.language = data.selectedLanguage?.code || 'en-US';
			}
		});
	}

	impersonate(user: ImpersonateUserCredentials): Observable<GlobalSettings> {
		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});

		const auth = () =>
			this.http
				.post(
					this.eupRoutesService.logonAs.url,
					{ contactId: user.contactId, businessPartnerId: user.businessPartnerId, sessionId: user.sessionId },
					{ headers, observe: 'response' }
				)
				.pipe(
					map((res: any) => {
						if (res.hasOwnProperty('body') && Object.keys(res.body).length > 0) {
							this.authStatus$.next({ isAuthenticated: true });
							return res.body as GlobalSettings;
						} else {
							// in case we want to redirect the user to another route, we need to get an error in order to make unsubscribe the login subscription
							return res.json();
						}
					})
				);

		return this.eupRoutesService.doAfterInit(auth);
	}

	getContactWithBusinessPartners(userName: string): Observable<ContactWithBusinessPartners[]> {
		this.ensureAuthInitialized();
		const params = new HttpParams().set('contactName', userName);

		return this.http.get(this.eupRoutesService.logonAs.getContacts, { params: params }, null, false);
	}

	getContactWithBusinessPartnersById(contactId: number): Observable<ContactWithBusinessPartners[]> {
		this.ensureAuthInitialized();
		const params = new HttpParams().set('contactId', contactId);

		return this.http.get(this.eupRoutesService.logonAs.getContactsById, { params: params }, null, false);
	}

	getDetails(): Observable<LoginDetails> {
		const getLogin = () => this.http.get(this.eupRoutesService.getLoginData(), undefined, undefined, false);
		return this.eupRoutesService.doAfterInit(getLogin, true);
	}

	isAuthenticated(): boolean {
		const settings = this.globalSettingsService.get();
		return !!settings;
	}

	async resetAuthInfo(timeout): Promise<void> {
		const userId = this.globalSettingsService.get()?.contactId;

		this.globalSettingsService.clear();
		await this.resetCache();
		this.authStatus$.next({
			isAuthenticated: false,
			isUnauthenticatedByTimeout: timeout,
			userId,
		});
		this.downloadNotificationService.clear();
		this.simulationStatusProgressStore.reset();
	}

	async logoutClient(params: LogoutParameters = {}): Promise<void> {
		this.timberService.info(`logout client called. logoutUrl: ${params.logoutUrl}`, { module: 'AuthService' });

		try {
			await this.resetAuthInfo(params.byTimeout);
			this.spinnerService.stop();
			if (params.logoutUrl && params.logoutUrl.trim() !== '') {
				this.timberService.info(`logout client. redirect to logoutUrl: ${params.logoutUrl}`, { module: 'AuthService' });

				if (params.returnUrl) {
					this.cookieService.set(Consts.CookiesNames.RETURNURL, params.returnUrl, Number.MAX_SAFE_INTEGER);
				}

				window.location.href = params.logoutUrl;
			} else {
				const queryParams: Params = { returnUrl: params.returnUrl };

				this.cookieService.set(Consts.CookiesNames.RETURNURL, '', Number.MAX_SAFE_INTEGER);
				this.platformLoginService.navigateToLogin({ queryParams });
			}
		} finally {
			this.appUpdater.checkForUpdates();
		}
	}

	logout(sessionId: string = '', params: LogoutParameters = {}): Observable<any> {
		return this.shouldUseNewLogoutFlow().pipe(
			switchMap(useNewFlow => 
				useNewFlow 
					? this.logoutNewFlow(sessionId, params) 
					: this.logoutLegacy(sessionId, params)
			),
			catchError((error) => {
				this.timberService.error('Error in logout, redirecting to login', { module: 'AuthService', error });
				return from(this.logoutClient(params));
			})
		);
	}

	private shouldUseNewLogoutFlow(): Observable<boolean> {
		const featureFlags = [FeatureToggle.IMSUserFlow, FeatureToggle.Auth0IsEnabled];
		return this.featuresToggleSettingsService.getFeaturesToggleSettings().pipe(
			take(1),
			map(res => featureFlags.every(f => res.some(ff => ff.id === f && ff.isActive))),
			catchError(() => from(this.logoutClient()).pipe(map(() => false))) // On error, fallback to '/logout'
		);
	}

	logoutNewFlow(sessionId: string = '', params: LogoutParameters = {}): Observable<any> {
		this.timberService.info(`logout new flow called. sessionId: ${sessionId}`, { module: 'AuthService' });

		const sessionInfoFromStorage = localStorage.getItem(Consts.Storage.SessionInfo);
		const sessionInfo = sessionInfoFromStorage ? (JSON.parse(sessionInfoFromStorage) as SessionInfo) : undefined;

		if (!sessionInfo) {
			this.timberService.error('No session info found in storage', { module: 'AuthService' });
			return from(this.logoutClient(params)).pipe(map(() => ({ })));
		}

		const UseIteroPlatformLogin = this.featuresToggleSettingsService.isVisibleFeature(FeatureToggle.UseIteroPlatformLogin, false);

		if (UseIteroPlatformLogin && this.platformLoginService.iteroPlatformLoginUsed()) {
			this.timberService.info(`logout via platform-login package. sessionId: ${sessionInfo.sessionId}`, { module: 'AuthService' });
			const username = this.globalSettingsService.get()?.loginEmail;
			return from(this.platformLoginService.logout(sessionInfo.idToken, sessionInfo.sessionId, username)).pipe(
				catchError((error) => {
					this.timberService.error('Error in platform-login package logout', { module: 'AuthService', error });
					return of({});
				}),
				finalize(() => this.logoutClient(params))
			);
		}

		const redirectUrl = `${window.location.origin.replace(/\/$/, '')}/login`;
		this.timberService.info(`logout new flow. redirectUrl: ${redirectUrl}`, { module: 'AuthService' });

		if (this.eupRoutesService.iTeroWebAuthApiUrl === null || this.eupRoutesService.iTeroWebAuthApiUrl === undefined) return of({});
		const url = `${this.eupRoutesService.iTeroWebAuthApiUrl.replace(/\/auth\/?$/, '')}/${Consts.LogoutPath}`;
		
			const body = {
			sessionId: sessionInfo.sessionId,
			appId: this.appId,
			redirectUri: redirectUrl,
			idToken: sessionInfo.idToken,
		};

		this.timberService.info(`logout new flow called. sessionId: ${sessionInfo.sessionId}`, { module: 'AuthService' });

		const headers = new HttpHeaders({
			[Consts.HeaderKeys.Authorization]: `Bearer ${sessionInfo.accessToken}`,
		});
		const context = new HttpContext().set(AUTH_ANGULAR__FORCE_REQUEST_WITH_EXPIRED_TOKEN, true);
		return this.http.post(url, body, { headers, context }).pipe(
			map((res) => {
				if (res.logoutUrl) {
					params.logoutUrl = res.logoutUrl;
				}
				this.timberService.info(`logout new flow. updated params: ${JSON.stringify(params)}`, { module: 'AuthService' });
				return {};
			}),
			catchError((error) => {
				this.timberService.error('Error in logoutNewFlow session manager request, falling back to base domain', { module: 'AuthService', error });
				params.logoutUrl = window.location.origin;
				return of({});
			}),
			finalize(() => {
				if (!params.logoutUrl) {
					this.timberService.warn('logoutNewFlow session-manager finalize: no logoutUrl, falling back to base domain', { module: 'AuthService' });
					params.logoutUrl = window.location.origin;
				}
				this.logoutClient(params);
			})
		);
	}

	relogin(returnUrl: string): Observable<any> {
		this.timberService.info(`relogin called. returnUrl: ${returnUrl}`, { module: 'AuthService' });
		const loginEmail = this.globalSettingsService.get()?.loginEmail;
		return this.shellContextService.getContext().pipe(
			take(1),
			switchMap(context => from(this.platformLoginService.relogin(returnUrl, loginEmail, context))),
			map(() => ({})),
			catchError((error) => {
				this.timberService.error('Error in relogin', { module: 'AuthService', error });
				return from(this.logoutClient({ returnUrl })).pipe(map(() => ({})));
			})
		);
	}

	logoutLegacy(sessionId: string = '', params: LogoutParameters = {}): Observable<any> {
		const sessionInfoFromStorage = localStorage.getItem(Consts.Storage.SessionInfo);
		const sessionInfo = sessionInfoFromStorage ? (JSON.parse(sessionInfoFromStorage) as SessionInfo) : undefined;

		const context = new HttpContext().set(AUTH_ANGULAR__FORCE_REQUEST_WITH_EXPIRED_TOKEN, true);
		return this.invalidateSession(sessionInfo, sessionId, { context }).pipe(
			catchError(() => {
				return of({});
			}),
			switchMap((_) => from(this.logoutClient(params)))
		);
	}

    invalidateOrLogoutSession(sessionId: string, params?: any): Observable<boolean> {
        const featureFlags = [FeatureToggle.IMSUserFlow, FeatureToggle.Auth0IsEnabled];

        return this.featuresToggleSettingsService.getFeaturesToggleSettings().pipe(
            take(1), 
            switchMap((res: FeatureToggleSettings[]) => {
                const areFeatureFlagsActive = featureFlags.every(
                    (f) => res.some((ff) => ff.id === f && ff.isActive)
                );

                if (areFeatureFlagsActive) {
					this.timberService.info("invalidateOrLogoutSession: Feature flags are active. Initiating new logout flow.", { module: 'AuthService' });
                    return this.logoutNewFlow(sessionId, params).pipe(
                        catchError((error) => {
							this.timberService.error("invalidateOrLogoutSession: Error in new logout flow, falling back to legacy:", { module: 'AuthService', error: error });
                            return of(false);
                        })
                    );
                } else {
                  	this.timberService.info("invalidateOrLogoutSession: Feature flags are off. Proceeding with invalidateOrLogoutSession.", { module: 'AuthService' });
					const sessionInfoFromStorage = localStorage.getItem(Consts.Storage.SessionInfo);
					const sessionInfo = sessionInfoFromStorage ? (JSON.parse(sessionInfoFromStorage) as SessionInfo) : undefined;
					return this.invalidateSession(sessionInfo, sessionId).pipe(
                        map((success: boolean) => {
							this.timberService.info(`invalidateOrLogoutSession: invalidateOrLogoutSession completed with success: ${success}`, { module: 'AuthService' });
                            return success; 
                        }),
                        catchError((error) => {
                            this.timberService.error("invalidateOrLogoutSession: Error during invalidateOrLogoutSession:", { module: 'AuthService', error: error });
							return of(false);
                        })
                    );
                }
            })
        );
    }

	invalidateSession(sessionInfo: SessionInfo | undefined, sessionId: string = '', options: {} = {}): Observable<boolean> {
		// Check if we should invalidate the session
		if (!sessionInfo || 
			sessionInfo.sessionId.trim() === '' || 
			(sessionId && sessionInfo.sessionId.trim() !== sessionId.trim())) {
			return of(false);
		}

		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});
		const sessionIdToInvalidate = sessionId && sessionId !== '' ? sessionId : sessionInfo?.sessionId;
		return this.http.deleteWithoutCancel(this.invalidateSessionUrl(sessionIdToInvalidate), { headers, observe: 'response', ...options }).pipe(
			map((res: any) => {
				return res.body;
			}),
			catchError((error) => {
				return of(false);
			})
		);
	}

	getCompanyIdFromCookie(): number | null {
		const returnUrlFromCookie = this.cookieService.get(Consts.CookiesNames.RETURNURL)?.toLowerCase();
		let companyFromCookie = null;
		
		if (returnUrlFromCookie && returnUrlFromCookie !== '') {
			companyFromCookie = +new URLSearchParams(returnUrlFromCookie.split('?')[1])?.get('companyid');
		}
		return companyFromCookie;
	}

	getDefaultCompany(companies: Company[]): Company {
		if (!companies) {
			this.timberService.error('getDefaultCompany called with undefined companies', { module: 'AuthService' });
		} else if (companies.length === 0) {
			this.timberService.error('getDefaultCompany called with empty companies array', { module: 'AuthService' });
		} else {
			this.timberService.info(`getDefaultCompany: processing ${companies.length} companies`, { 
				module: 'AuthService',
				extendedParameters: { companiesCount: companies.length.toString() }
			});
		}
		
		const companyIdFromCookie = this.getCompanyIdFromCookie();
		
		this.timberService.info('getDefaultCompany: Company selection logic', { 
			module: 'AuthService',
			extendedParameters: { 
				companyIdFromCookie: companyIdFromCookie?.toString() || 'none',
				hasMatchingCompany: (companyIdFromCookie && companies?.some(c => c.id === companyIdFromCookie))?.toString() || 'false',
				selectedCompanyId: companyIdFromCookie && companies?.some(c => c.id === companyIdFromCookie) 
					? companyIdFromCookie.toString() 
					: companies?.[0]?.id?.toString() || 'none'
			}
		});
		
		if (companyIdFromCookie && companies.some(c => c.id === companyIdFromCookie)) {
			return companies.find(c => c.id === companyIdFromCookie);
		}
		return companies[0];
	}

	isSameRegion(company: Company): boolean {
		if (!company.handlingEupUrl) return true;

		if (company.handlingEupUrl.endsWith('/')) {
			company.handlingEupUrl = company.handlingEupUrl.slice(0, -1);
		}

		const myIteroBffUrl = new URL(this.eupRoutesService.serverUrl);
		const companyMyIteroBffUrl = new URL(company.handlingEupUrl);
		
		if (myIteroBffUrl.host === companyMyIteroBffUrl.host) return true;
		return false;
	}
	
	redirect(company: Company): Observable<void> | null {
		const companyMyIteroBffUrl = new URL(company.handlingEupUrl);
		const currentPath = this.accessibilityService.getPathName();
		const currentSearchParams = new URLSearchParams(this.accessibilityService.getCurrentSearch());

		if (this.shouldConcatenateCompany(currentPath)) {
			currentSearchParams.set('companyid', company.id.toString());
		}

		const returnUrl = `${currentPath}?${currentSearchParams.toString()}`;
		const baseUrl = this.accessibilityService.getLocation().origin;
		const myiteroComUrl = new URL(`${baseUrl}/login?returnUrl=${encodeURIComponent(returnUrl)}`);

		// Return an observable that performs logout and redirect
		return this.logout().pipe(
			tap(() => {
				this.cookieService.set(Consts.CookiesNames.SWITCHREGIONURL, `${companyMyIteroBffUrl.origin}/`, Number.MAX_SAFE_INTEGER);
				this.cookieService.set(Consts.CookiesNames.RETURNURL, returnUrl, Number.MAX_SAFE_INTEGER);
				localStorage[Consts.Storage.RegionalApiEndpoint] = `${companyMyIteroBffUrl.origin}/`;
				this.accessibilityService.redirectTo(myiteroComUrl.href);
			}),
			map(() => void 0)
		);
	}

	private shouldConcatenateCompany(urlPathName: string): boolean {
		if (urlPathName.indexOf(`/viewer-wrapper/viewer`) > -1) {
			return false;
		} else {
			return true;
		}
	}

	private async resetCache(): Promise<void> {
		this.shellContextService.resetContext();
		this.stickyHeaderService.resetLogo();
		this.pendoService.closePendoInstance();
		await this.featuresToggleSettingsService.resetFeaturesToggles();
		this.softwareOptionsService.resetCompanySoftwareOptions();
		this.applicationNavigationService.reset();
		this.labService.reset();
		this.cookieAuthService.removeAllCookies();
		localStorage.removeItem(Consts.Storage.Settings);
		localStorage.removeItem(Consts.Storage.SessionInfo);
		sessionStorage.clear();
	}

	private throwError(error: any) {
		const newError = new Error(error.message);
		newError['status'] = error.status;
		return throwError(() => newError);
	}

	private setCookies(sessionInfo: SessionInfo, companyType: CompanyType, authProvider: AuthProvider, companyId: number) {
		this.cookieAuthService.setCookie(CookieName.COMPANYID, companyId);
		this.cookieAuthService.setCookie(CookieName.COMPANYTYPES, companyType);
		this.cookieAuthService.setCookie(CookieName.REGION, this.utils.getRegion());
		this.cookieAuthService.setCookie(CookieName.IDENTITYPROVIDER, authProvider);
		this.cookieAuthService.setCookie(CookieName.SESSIONID, sessionInfo.sessionId);
		this.cookieAuthService.setCookie(CookieName.ITEROTOKEN, sessionInfo.accessToken);
		this.cookieAuthService.setCookie(CookieName.IDTOKEN, sessionInfo.idToken);
	}
}

export class UserCredentials {
	username: string;
	password: string;

	constructor(username: string, password: string) {
		this.username = username;
		this.password = password;
	}
}

export interface SessionInfo {
	sessionId: string;
	accessToken: string;
	sessionType: string;
	idToken: string;
}

export class ImpersonateUserCredentials {
	contactId: number;
	businessPartnerId: number;
	sessionId?: string;

	constructor(contactId: number, businessPartnerId: number, sessionId?: string) {
		this.businessPartnerId = businessPartnerId;
		this.contactId = contactId;
		this.sessionId = sessionId;
	}
}

export class LoginDetails {
	appVersion: string;
	bffVersion: string;
	databaseVersion: string;
	forgotPasswordUrl: string;
	dbInfo: string;
	region: string;
	regionsEndpoints: { [key: string]: string };
}
